import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FileInput, Map, ClipboardList, Scissors,
  FileText, Pill, Syringe, Wrench, Activity, Wallet, BarChart3, Coins,
  Bot, Settings, LogOut, Building2, UserCog, Menu, Hospital, Plus, Sparkles, HandHeart, Upload
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { EmpresaSelector } from "@/components/EmpresaSelector";
import { useState } from "react";

const NAV = [
  { to: "/painel", label: "Painel", icon: LayoutDashboard, perm: "dashboard:view" },
  { to: "/pacientes", label: "Pacientes", icon: Users, perm: "patients:view" },
  { to: "/mapa-bruto", label: "Mapa Bruto", icon: FileInput, perm: "raw_maps:edit" },
  { to: "/mapa-filtrado", label: "Mapa Filtrado", icon: Map, perm: "filtered_maps:edit" },
  { to: "/casos", label: "Casos Cirúrgicos", icon: Scissors, perm: "cases:edit" },
  { to: "/procedimentos", label: "Procedimentos", icon: ClipboardList, perm: "procedures:edit" },
  { type: "separator", label: "Cadastros" },
  { to: "/cirurgioes", label: "Cirurgiões", icon: UserCog, perm: "surgeons:edit" },
  { to: "/anestesistas", label: "Anestesistas", icon: Syringe, perm: "surgeons:edit" },
  { to: "/instrumentadores", label: "Instrumentadores", icon: Wrench, perm: "surgeons:edit" },
  { to: "/hospitais", label: "Hospitais", icon: Hospital, perm: "hospitals:edit" },
  { type: "separator", label: "Gestão" },
  { to: "/producao", label: "Produção", icon: Activity, perm: "production:view" },
  { to: "/faturamento", label: "Faturamento", icon: Wallet, perm: "billing:view" },
  { to: "/financeiro", label: "Financeiro", icon: Coins, perm: "financial:view" },
  { to: "/controle-bvsm", label: "Controle BVSM", icon: Building2, perm: "bvsm_control:view" },
  { to: "/pro-bono", label: "Pro Bono", icon: HandHeart, perm: "production:view" },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, perm: "reports:view" },
  { to: "/analytics", label: "BI / Análise", icon: Sparkles, perm: "analytics:view" },
  { to: "/assistente-ia", label: "Assistente IA", icon: Bot, perm: "ai:use" },
  { type: "separator", label: "Sistema" },
  { to: "/aliases", label: "Aprendizado", icon: Sparkles, perm: "procedures:edit" },
  { to: "/importar", label: "Importar em massa", icon: Upload, perm: "procedures:edit" },
  { to: "/empresas", label: "Empresas", icon: Building2, superAdminOnly: true },
  { to: "/usuarios", label: "Usuários", icon: Users, perm: "users:manage" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, perm: "settings:view" },
];

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${item.to.replace("/", "")}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-md px-3 py-3 md:py-2 text-sm transition-colors touch-manipulation ${
          isActive
            ? "bg-blue-50 text-blue-700 font-medium"
            : "text-slate-700 hover:bg-slate-100 active:bg-slate-200 hover:text-blue-700"
        }`
      }
    >
      <Icon className="h-5 w-5 md:h-4 md:w-4" strokeWidth={1.75} />
      <span className="flex-1">{item.label}</span>
      {item.pending && (
        <span className="text-[10px] uppercase tracking-wider text-slate-400">em breve</span>
      )}
    </NavLink>
  );
}

function SidebarContent({ onNavigate }) {
  const { user, logout, can } = useAuth();
  // Group items so we can hide separators that have no following items.
  const visible = [];
  let lastWasSeparator = false;
  NAV.forEach((item) => {
    if (item.type === "separator") {
      // Defer adding — only push when the next visible item shows up.
      lastWasSeparator = item;
      return;
    }
    if (item.perm && !can(item.perm)) return;
    if (item.superAdminOnly && !user?.is_super_admin) return;
    if (lastWasSeparator) {
      visible.push(lastWasSeparator);
      lastWasSeparator = false;
    }
    visible.push(item);
  });

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="px-5 py-5 border-b border-slate-200">
        <img src="/brand/apex-logo.png" alt="APEX MEDICAL HUB" className="h-10 w-auto object-contain" />
        <div className="text-xs text-slate-500 mt-1.5">BV Serviços Médicos</div>
      </div>
      <EmpresaSelector />
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
        {visible.map((item) => {
          if (item.type === "separator") {
            return (
              <div key={`sep-${item.label}`} className="pt-3 pb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {item.label}
              </div>
            );
          }
          return <NavItem key={item.to} item={item} onClick={onNavigate} />;
        })}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <div className="flex items-center gap-3 rounded-md bg-slate-50 p-2.5">
          <div className="h-8 w-8 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center">
            {user?.name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.role_label || user?.role}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-slate-500 hover:text-red-600"
            onClick={logout}
            data-testid="btn-logout"
            title="Sair"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Sidebar({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 md:border-r md:border-slate-200 md:min-h-screen md:fixed md:inset-y-0">
        <SidebarContent />
      </aside>
      {/* Main content */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen min-w-0">
        {/* Mobile header */}
        <header className="md:hidden flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 sticky top-0 z-20">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="btn-mobile-menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <img src="/brand/apex-symbol.png" alt="Apex" className="h-7 w-auto object-contain" />
          <div className="font-display font-bold text-slate-900">BV Serviços Médicos</div>
        </header>
        <main className="flex-1 p-3 sm:p-6 lg:p-8 pb-safe">{children}</main>
      </div>
    </div>
  );
}
